import { Card } from '@/components/ui/Card';
import { TrendingUp, Receipt, RotateCcw, Package, Wallet, AlertTriangle } from 'lucide-react';
import { formatIDR, formatNumber } from '@/lib/format';
import type { EventSummary } from '@/lib/api/reports';

export function SummaryCards({ summary }: { summary: EventSummary }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card padding="md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Total sales</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                {formatIDR(summary.totalSales)}
              </p>
            </div>
            <div className="w-10 h-10 rounded bg-success/10 text-success flex items-center justify-center">
              <TrendingUp size={18} />
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Transaksi</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                {formatNumber(summary.totalTransactions)}
              </p>
              <p className="text-caption text-ink-subtle mt-1">
                Avg: {formatIDR(summary.averageTransactionValue)}
              </p>
            </div>
            <div className="w-10 h-10 rounded bg-primary/10 text-primary flex items-center justify-center">
              <Receipt size={18} />
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Item terjual</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                {formatNumber(summary.totalItems)}
              </p>
              {/* totalItems is already net of returns, so this caption explains
                  that figure rather than inviting a second subtraction. It also
                  counts units — the old version showed returnsCount, which is a
                  count of return invoices, a different unit entirely. */}
              <p className="text-caption text-ink-subtle mt-1">
                {summary.returnedItems > 0
                  ? `Sudah dikurangi ${formatNumber(summary.returnedItems)} item retur`
                  : 'Belum ada retur'}
              </p>
            </div>
            <div className="w-10 h-10 rounded bg-warning/10 text-warning flex items-center justify-center">
              <Package size={18} />
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Returns</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                {formatIDR(summary.returnsTotal)}
              </p>
              <p className="text-caption text-ink-subtle mt-1">
                {formatNumber(summary.returnsCount)} invoice
              </p>
            </div>
            <div className="w-10 h-10 rounded bg-danger/10 text-danger flex items-center justify-center">
              <RotateCcw size={18} />
            </div>
          </div>
        </Card>
      </div>

      {/* Receivables (piutang) — only render if there's any tempo activity */}
      {(summary.tempoCount > 0 || summary.lunasCount > 0) && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline uppercase text-ink-subtle font-medium">
                  Piutang outstanding
                </p>
                <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                  {formatIDR(summary.outstandingTotal)}
                </p>
                <p className="text-caption text-ink-subtle mt-1">
                  Sisa yang belum dibayar
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-warning/10 text-warning flex items-center justify-center">
                <Wallet size={18} />
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline uppercase text-ink-subtle font-medium">Overdue</p>
                <p
                  className={`text-metric font-display font-semibold mt-2 tabular-nums ${
                    summary.overdueCount > 0 ? 'text-danger' : 'text-ink'
                  }`}
                >
                  {formatNumber(summary.overdueCount)}
                </p>
                <p className="text-caption text-ink-subtle mt-1">
                  Lewat jatuh tempo
                </p>
              </div>
              <div
                className={`w-10 h-10 rounded flex items-center justify-center ${
                  summary.overdueCount > 0
                    ? 'bg-danger/10 text-danger'
                    : 'bg-line/40 text-ink-subtle'
                }`}
              >
                <AlertTriangle size={18} />
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline uppercase text-ink-subtle font-medium">Tempo</p>
                <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                  {formatNumber(summary.tempoCount)}
                </p>
                <p className="text-caption text-ink-subtle mt-1">
                  Piutang · {formatNumber(summary.pendingPaymentCount)} belum jatuh tempo
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-info/10 text-info flex items-center justify-center">
                <Wallet size={18} />
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline uppercase text-ink-subtle font-medium">Lunas</p>
                <p className="text-metric font-display font-semibold mt-2 text-ink tabular-nums">
                  {formatNumber(summary.lunasCount)}
                </p>
                <p className="text-caption text-ink-subtle mt-1">
                  Bayar lunas di tempat
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-success/10 text-success flex items-center justify-center">
                <Receipt size={18} />
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
